import CloseIcon from '@mui/icons-material/Close';
import { Box, Dialog, IconButton, Typography } from '@mui/material';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts/highstock';
import { FunctionComponent } from 'react';
import { useAppTranslate } from '../../../../core/hooks';
import { Loader } from '../../../../ui/components';
import { StatisticsTableChartOptions } from '../../../chart-settings/components';
import { useChartSettings } from '../../../chart-settings/hooks';
import { ChartSettings } from '../../../chart-settings/model';
import { useSlaMetricsMeasurementApiGet } from '../../hooks';
import { buildSeriesData, SlaMetricsMeasurementViewModel, SlaMonitoredClassOfServiceViewModel } from '../../model';
import { useTimeSeriesStatisticsHighchartsOptions } from '../../../timeseries-statistics/hooks/TimeSeriesStatistics.hook';

interface Props {
  initialChartSettings: ChartSettings;
  initialChartData: SlaMetricsMeasurementViewModel;
  classOfService: SlaMonitoredClassOfServiceViewModel;
  metric: 'jitter' | 'delay' | 'lossRatio';
  unit: 'millisecond' | 'percent';
  open: boolean;
  onClose: () => void;
  customerId: number;
}

export const SlaStatisticsTableChartDialog: FunctionComponent<Props> = (
  { classOfService, initialChartData, metric, unit, open, onClose, initialChartSettings, customerId },
) => {
  const chartSettingsInteraction = useChartSettings({
    timespan: initialChartSettings.timespan,
    resolution: initialChartSettings.resolution,
  });
  const chartSettings = chartSettingsInteraction.chartSettings;
  const {
    measurement,
    isFetched,
    isFetching,
  } = useSlaMetricsMeasurementApiGet(
    customerId,
    classOfService.itemId,
    classOfService.cosId,
    chartSettings,
    'chart',
  );
  const effectiveChartData = measurement || initialChartData;
  const translate = useAppTranslate();
  const seriesName = `${translate('tableHeaders.' + metric)}`;
  const unitTranslated = translate('units.' + unit);
  const seriesData = buildSeriesData(effectiveChartData, metric);
  const highchartsOptions = useTimeSeriesStatisticsHighchartsOptions(
    chartSettings,
    unitTranslated,
    [{ seriesData, seriesName }]);

  return <>
    <Dialog open={open} onClose={onClose} disableEscapeKeyDown={true} maxWidth={'xl'} fullWidth={true}>
      <Box sx={{ p: { xs: '2rem', sm: '6rem' } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', pb: 2 }}>
          <Box>
            <Typography variant={'h5'}>{classOfService.serviceName} | {translate(`tableHeaders.${metric}`)}</Typography>
            <Typography variant={'h6'}>{translate(`tableHeaders.sozId`)}: {classOfService.orderNumber} | {classOfService.classOfServiceName}</Typography>
          </Box>
          <Box>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
        <StatisticsTableChartOptions chartSettingsInteraction={chartSettingsInteraction} />
        {isFetching && <Box sx={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Loader loading={isFetching} />
        </Box>}
        {effectiveChartData && isFetched && !isFetching && <HighchartsReact
          highcharts={Highcharts}
          options={highchartsOptions}
        />}
      </Box>
    </Dialog>
  </>;
};